import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTRPC } from "@/trpc";
import type { RunReport, RunScope } from "../types";

/** A source waiting to retry can be taken over by staff: this stops its
 * automatic retries so they can upload evidence and decide the item. */
export function StopRetryingButton({
  check,
  runId,
  scope,
}: {
  check: RunReport["checks"][number];
  runId: string;
  scope: RunScope;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const start = useMutation(
    trpc.psv.review.stopRetrying.mutationOptions({
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: trpc.psv.getRun.queryKey({ ...scope, runId }),
        }),
    }),
  );
  return (
    <div className="space-y-1.5">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={start.isPending}
        aria-label={`Stop retrying and review ${check.authorityName}`}
        onClick={() =>
          start.mutate({
            organizationBusinessUnitId: scope.organizationBusinessUnitId,
            providerUserId: scope.providerUserId,
            expectedAffiliationId: scope.expectedAffiliationId,
            runId,
            checkId: check.id,
            expectedCheckRevision: check.revision,
          })
        }
      >
        {start.isPending && (
          <Loader2
            className="mr-2 size-4 motion-safe:animate-spin"
            aria-hidden
          />
        )}
        Stop retrying and review
      </Button>
      {start.isError && (
        <p role="alert" className="text-xs text-destructive">
          {start.error.message}
        </p>
      )}
    </div>
  );
}
